import { FC, useState, useEffect } from "react";
import { Marker } from "@react-google-maps/api";
import { OrderStatus } from "../../../../types/order/IOrder";
import { MapMarkersProps } from "../types";

export const MapMarkers: FC<MapMarkersProps> = ({
  selectedRoute,
  driverPosition,
  startPin,
  waypointPin,
  driverPin,
  onStopSelect,
}) => {
  const [visibleMarkers, setVisibleMarkers] = useState<number[]>([]);
  const [hoverMarker, setHoverMarker] = useState<number | null>(null);

  // Animate markers sequentially when route changes
  useEffect(() => {
    if (!selectedRoute?.route?.length) {
      setVisibleMarkers([]);
      return;
    }

    // Reset visible markers
    setVisibleMarkers([]);

    const animationDelay = 100; // ms between each marker

    // Show markers one by one
    const timers: NodeJS.Timeout[] = [];

    selectedRoute.route.forEach((_, index) => {
      const timer = setTimeout(() => {
        setVisibleMarkers((prev) => [...prev, index]);
      }, index * animationDelay);

      timers.push(timer);
    });

    return () => {
      // Clean up timers
      timers.forEach((timer) => clearTimeout(timer));
    };
  }, [selectedRoute?.id, selectedRoute?.route]);

  // Create circular icon for markers
  const createCircularIcon = (
    isPickup: boolean,
    isHovered: boolean,
    isCompleted: boolean,
  ): google.maps.Icon => {
    const size = isHovered ? 34 : 30;

    // Use muted colors for completed stops, original colors for active stops
    const baseColor = isCompleted
      ? "#6B7280" // gray-500 for completed - matches polyline color
      : isPickup
        ? "#7214FF" // lgb-primary for pickups
        : "#0E9F6E"; // lgb-main-green for deliveries

    const gradientColor = isCompleted
      ? "#9CA3AF" // gray-400 for completed gradient
      : isPickup
        ? "#918cf3" // lgb-primary-lighten
        : "#11be83"; // lgb-main-green-lighten

    const shadowColor = isCompleted
      ? "#6B728040" // darker shadow for completed
      : isPickup
        ? "#7214FF40"
        : "#0E9F6E40"; // Semi-transparent version

    // Create modern SVG with gradient and shadow
    const svg = `
      <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="gradient-${isPickup ? "pickup" : "delivery"}-${isCompleted ? "completed" : "active"}" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:${gradientColor};stop-opacity:1" />
            <stop offset="100%" style="stop-color:${baseColor};stop-opacity:1" />
          </linearGradient>
          <filter id="shadow-${isPickup ? "pickup" : "delivery"}-${isCompleted ? "completed" : "active"}" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="2" stdDeviation="3" flood-color="${shadowColor}" flood-opacity="${isCompleted ? "0.2" : "0.3"}"/>
          </filter>
        </defs>
        <!-- Outer ring for depth -->
        <circle 
          cx="${size / 2}" 
          cy="${size / 2}" 
          r="${size / 2 - 1}" 
          fill="white" 
          filter="url(#shadow-${isPickup ? "pickup" : "delivery"}-${isCompleted ? "completed" : "active"})"
        />
        <!-- Main circle with gradient -->
        <circle 
          cx="${size / 2}" 
          cy="${size / 2}" 
          r="${size / 2 - 3}" 
          fill="url(#gradient-${isPickup ? "pickup" : "delivery"}-${isCompleted ? "completed" : "active"})" 
          stroke="white" 
          stroke-width="2"
        />
        <!-- Inner highlight for modern look -->
        <circle 
          cx="${size / 2 - 2}" 
          cy="${size / 2 - 2}" 
          r="${size / 4}" 
          fill="rgba(255,255,255,${isCompleted ? "0.1" : "0.2"})" 
        />
      </svg>
    `;

    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
      scaledSize: new window.google.maps.Size(size, size),
      origin: new window.google.maps.Point(0, 0),
      anchor: new window.google.maps.Point(size / 2, size / 2),
      labelOrigin: new window.google.maps.Point(size / 2, size / 2),
    };
  };

  // Create custom marker with pulse effect
  const createMarker = (
    index: number,
    position: google.maps.LatLngLiteral,
    stopNumber: number,
    isPickup: boolean,
    isCompleted: boolean,
  ) => {
    const isVisible = visibleMarkers.includes(index);
    const isHovered = hoverMarker === index;
    const isStart = index === 0;

    // Skip if not visible yet (for animation)
    if (!isVisible) return null;

    const circularIcon = createCircularIcon(isPickup, isHovered, isCompleted);

    return (
      <div key={`marker-wrapper-${index}`}>
        <Marker
          key={`marker-${index}`}
          position={position}
          icon={circularIcon}
          label={{
            text: stopNumber.toString(),
            color: "white",
            fontSize: "12px",
            fontWeight: "bold",
            className: "map-marker-label",
          }}
          onClick={() => onStopSelect(selectedRoute!.route[index])}
          onMouseOver={() => setHoverMarker(index)}
          onMouseOut={() => setHoverMarker(null)}
          animation={google.maps.Animation.DROP}
          zIndex={isHovered ? 2000 : isCompleted ? 200 : isStart ? 1000 : 1000} // Higher z-index for active stops
        />

        {/* Pulse effect for start marker or hovered markers (only for non-completed) */}
        {(isStart || isHovered) && !isCompleted && (
          <div className="absolute">
            <div
              className={`absolute rounded-full animate-ping ${
                isPickup ? "bg-lgb-primary/30" : "bg-lgb-main-green/30"
              }`}
              style={{
                width: "36px",
                height: "36px",
                left: position.lat,
                top: position.lng,
                animationDuration: isStart ? "2s" : "1.5s",
                transform: "translate(-50%, -50%)",
              }}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {selectedRoute?.route.map((stop, index) => {
        const position = {
          lat: stop.location.coordinate?.lat ?? 0,
          lng: stop.location.coordinate?.lon ?? 0,
        };

        const isCompleted = stop.status === OrderStatus.Completed;

        return createMarker(
          index,
          position,
          stop.stopNumber,
          stop.isPickup ?? false,
          isCompleted,
        );
      })}

      {driverPosition && (
        <Marker
          position={driverPosition}
          icon={driverPin}
          animation={google.maps.Animation.DROP}
          zIndex={3000} // Driver position always on top
          title="Driver Current Position"
        />
      )}
    </>
  );
};
